import { db } from '@app/icare/client/db'
import {
  localPaginatedQueryOptions,
  type ResultTypeFromQueryResult,
} from '@app/icare/client/db/client/localQueryOptions'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientFormTypeEnum, type PatientFormTypeType } from '@app/icare/db/schema/form/PatientFormType'
import {
  type PatientIdType,
  type PatientStatus,
  PatientStatusEnum,
  type PatientStatusType,
} from '@app/icare/entities/patient/schema/Patient'
import { SignFormStatusEnum, type SignFormStatusType } from '@app/icare/entities/patientForm/schema/PatientForm'
import { UserInfo } from '@app/icare/entities/user/components/UserInfo'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { formatDate, formatDateTime } from '@pkg/shared/utils'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Descriptions, Space, Table } from 'antd'
import { sql } from 'kysely'
import { jsonObjectFrom } from 'kysely/helpers/sqlite'
import { useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/signed')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z
    .enum(['patientForm.type', 'patient.personal.lastName', 'patientForm.createdAt', 'patientForm.status'])
    .default('patientForm.createdAt')
    .nullish(),
  filters: z
    .object({
      'patientForm.status': Q.in(z.enum(SignFormStatusEnum.$.zodEnum())).nullish(),
      'patientForm.type': Q.in(z.enum(PatientFormTypeEnum.$.zodEnum())).nullish(),
      'patient.status': Q.in(z.enum(PatientStatusEnum.$.zodEnum())).nullish(),
    })
    .optional(),
})

const PatientStatusValueSchema = z.enum(PatientStatusEnum.$.zodEnum())
const INITIAL_FILTERS = {
  'patientForm.status': F.in([SignFormStatusEnum.new]),
  'patient.status': F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
}

const useSignedQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
  },
})

const validTypes: PatientFormTypeType[] = [
  PatientFormTypeEnum.PatientInformation,
  PatientFormTypeEnum.PatientConsentAndConfidentiality,
  PatientFormTypeEnum.PatientSupply,
]
function RouteComponent() {
  const queryStore = useSignedQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const signedParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const patientStatusFilter = signedParams.filters.find(f => f.field === 'patient.status')
  const patientStatusValues: PatientStatusType[] =
    patientStatusFilter?.operator === 'in' && Array.isArray(patientStatusFilter.value)
      ? patientStatusFilter.value.flatMap(value => {
          const parsed = PatientStatusValueSchema.safeParse(value)
          return parsed.success ? [parsed.data] : []
        })
      : patientStatusFilter?.operator === 'eq'
        ? (() => {
            const parsed = PatientStatusValueSchema.safeParse(patientStatusFilter.value)
            return parsed.success ? [parsed.data] : []
          })()
        : []
  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll('patientForm')
        .select(sb =>
          jsonObjectFrom(
            sb
              .selectFrom('patient')
              .select(['id', 'personal', 'contact', 'status', 'address'])
              .whereRef('patient.id', '=', 'patientForm.patientId'),
          ).as('patient'),
        )
        .where('patientForm.type', 'in', [
          PatientFormTypeEnum.PatientInformation,
          PatientFormTypeEnum.PatientConsentAndConfidentiality,
          PatientFormTypeEnum.PatientSupply,
        ])
        .$if(patientStatusValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patient')
                .select('patient.id')
                .whereRef('patient.id', '=', 'patientForm.patientId')
                .where(eb => eb(eb.ref('patient.status', '->>').key('value'), 'in', patientStatusValues)),
            ),
          ),
        )
        .$if(signedParams.sortBy === 'patient.personal.lastName', qb =>
          qb.orderBy(
            sql`(
              select json_extract("patient"."personal", '$.lastName')
              from "patient"
              where "patient"."id" = "patientForm"."patientId"
            )`,
            signedParams.sortDir,
          ),
        )
        .$call(queryKit.filters(signedParams, ['patientForm.status', 'patientForm.type']))
        .$call(queryKit.search(signedParams))
        .$call(queryKit.deleted(signedParams.deleted, db.dynamic.ref('patientForm.deletedAt')))
        .$if(signedParams.sortBy !== 'patient.personal.lastName', qb => qb.$call(queryKit.sort(signedParams)))
        .$call(queryKit.paginate(signedParams)),
      params: signedParams,
    }),
  )

  type SignedRecord = ResultTypeFromQueryResult<typeof data>

  const columns = useColumnDef<SignedRecord>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) =>
          record.patient && (
            <FormattedPatientNameAndAddress
              patient={{
                ...record.patient,
                id: record.patient.id as PatientIdType,
                status: record.patient.status as PatientStatus,
              }}
            />
          ),
      },
      {
        key: 'patientForm.status',
        title: t`Status`,
        sorter: true,
        render: (_, record) => (
          <FormStatusBadge<SignFormStatusType>
            value={record.status as SignFormStatusType}
            statusEnum={SignFormStatusEnum}
          />
        ),
      },
      {
        key: 'patientForm.createdAt',
        title: <Trans>Erstellt am</Trans>,
        sorter: true,
        render: (_, record) => formatDate(record.createdAt),
      },
      {
        key: 'patientForm.type',
        title: <Trans>Dokument</Trans>,
        sorter: true,
        render: (_, record) => PatientFormTypeEnum.$.getLabel(record.type),
      },
      {
        key: 'patientForm.id',
        title: <Trans>Aktionen</Trans>,
        fixed: 'end',
        sorter: false,
        render: (_, record) => {
          if (!validTypes.includes(record.type)) return null

          const documentPath =
            record.type === PatientFormTypeEnum.PatientInformation
              ? 'patienten-information'
              : record.type === PatientFormTypeEnum.PatientConsentAndConfidentiality
                ? 'einwilligung-schweigepflicht'
                : record.type === PatientFormTypeEnum.PatientSupply
                  ? 'versorgungsauftrag'
                  : ''
          const link = `/patienten/${record.patientId}/${documentPath}/${record.id}`

          return (
            <ButtonLink to={link as never} size="small">
              <Trans>zum Dokument</Trans>
              <LinkIndicatorIcon />
            </ButtonLink>
          )
        },
      },
    ],
    [],
    useSignedQueryStore,
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )
  return (
    <QueryFilterWrapper
      queryStore={useSignedQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status',
          mode: 'multiple',
          label: <Trans>Status der Patientenakte</Trans>,
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'patientForm.status',
          mode: 'multiple',
          label: <Trans>Status des Dokuments</Trans>,
          options: SignFormStatusEnum.$.options,
        },
        {
          name: 'patientForm.type',
          mode: 'multiple',
          label: <Trans>Dokument</Trans>,
          options: PatientFormTypeEnum.$.options.filter(option => validTypes.includes(option.value)),
          direction: 'vertical',
        },
      ]}
    >
      <Table
        dataSource={data?.data ?? []}
        loading={isFetching}
        rowKey="id"
        pagination={data}
        onChange={queryStore.onChange}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: record => (
            <Descriptions
              column={1}
              size="small"
              style={{ paddingLeft: 32 }}
              items={[
                {
                  label: t`Erstellt von`,
                  key: 'createdBy',
                  children: <UserInfo userId={record.createdBy} />,
                },
                {
                  label: t`Erstellt am`,
                  key: 'createdAt',
                  children: formatDateTime(record.createdAt),
                },
                {
                  label: t`Geändert von`,
                  key: 'updatedBy',
                  children: <UserInfo userId={record.updatedBy} />,
                },
                {
                  label: t`Geändert am`,
                  key: 'updatedAt',
                  children: formatDateTime(record.updatedAt),
                },
              ]}
            />
          ),
        }}
      />
    </QueryFilterWrapper>
  )
}
